import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, TrendingUp, Assignment, Group } from '@mui/icons-material';
import OKRService, { OKR, OKRGeracaoDto } from '../services/OKRService';
import { PrimaryButton, SecondaryButton } from './index';

const OKRList: React.FC = () => {
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openGerarDialog, setOpenGerarDialog] = useState(false);
  const [novoOKR, setNovoOKR] = useState<Partial<OKR>>({
    nome: '',
    descricao: '',
    tipo: 'ESTRATEGICO',
    status: 'ABERTO',
    responsavel: ''
  });
  const [geracaoData, setGeracaoData] = useState<OKRGeracaoDto>({
    quantidadeOKRs: 4
  });

  useEffect(() => {
    carregarOKRs();
  }, []);

  const carregarOKRs = async () => {
    try {
      setLoading(true);
      const dados = await OKRService.listar();
      setOkrs(dados);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar OKRs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCriarOKR = async () => {
    try {
      if (!novoOKR.nome || !novoOKR.descricao) {
        setError('Nome e descrição são obrigatórios');
        return;
      }

      await OKRService.criar(novoOKR as Omit<OKR, 'id'>);
      setOpenDialog(false);
      setNovoOKR({
        nome: '',
        descricao: '',
        tipo: 'ESTRATEGICO',
        status: 'ABERTO',
        responsavel: ''
      });
      carregarOKRs();
    } catch (err) {
      setError('Erro ao criar OKR');
      console.error(err);
    }
  };

  const handleGerarOKRs = async () => {
    try {
      const okrsGerados = await OKRService.gerarOKRs(geracaoData);
      
      // Salvar cada OKR gerado
      for (const okr of okrsGerados) {
        await OKRService.criar(okr);
      }
      
      setOpenGerarDialog(false);
      carregarOKRs();
    } catch (err) {
      setError('Erro ao gerar OKRs');
      console.error(err);
    }
  };

  const getTipoColor = (tipo: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
      'ESTRATEGICO': 'primary',
      'CAPACIDADES_INTERNAS': 'secondary',
      'APROVEITAMENTO_OPORTUNIDADES': 'success',
      'MITIGACAO_RISCOS': 'warning',
      'TATICO': 'info',
      'CULTURA_PESSOAS': 'error'
    };
    return colors[tipo] || 'primary';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
      'ABERTO': 'info',
      'EM_PROGRESSO': 'warning',
      'CONCLUIDO': 'success',
      'CANCELADO': 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Carregando OKRs...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          OKRs (Objectives and Key Results)
        </Typography>
        <Box>
          <PrimaryButton
            variant="outlined"
            startIcon={<TrendingUp />}
            onClick={() => setOpenGerarDialog(true)}
            sx={{ mr: 2 }}
          >
            Gerar com IA
          </PrimaryButton>
          <PrimaryButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Novo OKR
          </PrimaryButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {okrs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Assignment sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum OKR encontrado
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Comece criando seu primeiro OKR ou use a IA para gerar sugestões
            </Typography>
            <PrimaryButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Criar Primeiro OKR
            </PrimaryButton>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {okrs.map((okr) => (
            <Grid size={{xs: 12, md: 6, lg: 4}} key={okr.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  title={okr.nome}
                  subheader={
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={okr.tipo.replace(/_/g, ' ')}
                        color={getTipoColor(okr.tipo)}
                        size="small"
                      />
                      <Chip
                        label={okr.status.replace(/_/g, ' ')}
                        color={getStatusColor(okr.status)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {okr.descricao}
                  </Typography>
                  
                  {okr.responsavel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Group sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">
                        Responsável: {okr.responsavel}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Progresso</Typography>
                      <Typography variant="body2">{okr.progresso || 0}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={okr.progresso || 0}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>

                  {okr.keyResults && okr.keyResults.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {okr.keyResults.length} Key Result(s)
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog para criar novo OKR */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Criar Novo OKR</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome do Objetivo"
              value={novoOKR.nome}
              onChange={(e) => setNovoOKR({ ...novoOKR, nome: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={3}
              value={novoOKR.descricao}
              onChange={(e) => setNovoOKR({ ...novoOKR, descricao: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={novoOKR.tipo}
                label="Tipo"
                onChange={(e) => setNovoOKR({ ...novoOKR, tipo: e.target.value as any })}
              >
                <MenuItem value="ESTRATEGICO">Estratégico</MenuItem>
                <MenuItem value="CAPACIDADES_INTERNAS">Capacidades Internas</MenuItem>
                <MenuItem value="APROVEITAMENTO_OPORTUNIDADES">Aproveitamento de Oportunidades</MenuItem>
                <MenuItem value="MITIGACAO_RISCOS">Mitigação de Riscos</MenuItem>
                <MenuItem value="TATICO">Tático</MenuItem>
                <MenuItem value="CULTURA_PESSOAS">Cultura e Pessoas</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Responsável"
              value={novoOKR.responsavel}
              onChange={(e) => setNovoOKR({ ...novoOKR, responsavel: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setOpenDialog(false)}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={handleCriarOKR}>
            Criar OKR
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Dialog para gerar OKRs com IA */}
      <Dialog open={openGerarDialog} onClose={() => setOpenGerarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gerar OKRs com IA</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Quantidade de OKRs"
              type="number"
              value={geracaoData.quantidadeOKRs}
              onChange={(e) => setGeracaoData({ ...geracaoData, quantidadeOKRs: parseInt(e.target.value) })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Contexto Adicional"
              multiline
              rows={3}
              value={geracaoData.contextoAdicional || ''}
              onChange={(e) => setGeracaoData({ ...geracaoData, contextoAdicional: e.target.value })}
              placeholder="Descreva contexto específico para a geração dos OKRs..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setOpenGerarDialog(false)}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={handleGerarOKRs}>
            Gerar OKRs
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OKRList;
